/**
 * プライベートメモのエクスポート/インポート機能
 * セキュリティ重視の多層防御アーキテクチャ
 */

import type { AnyService } from "../types";
import i18n from "./i18n";
import { type EncryptedNotesEnvelope, isEncryptedEnvelope } from "./privateNotesCrypto";

// ===== 定数定義 =====
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const MAX_NOTES_COUNT = 1000; // 最大メモ件数
const MAX_NOTE_LENGTH = 100; // 1メモの最大文字数
const EXPORT_VERSION = "1.0.0"; // データフォーマットバージョン

// ===== 型定義 =====
export interface PrivateNotesExportData {
  version: string;
  exportedAt: string;
  notes: Record<string, string>;
  metadata: {
    totalNotes: number;
    notesList: Array<{
      serviceId: string;
      serviceName?: string;
      noteLength: number;
    }>;
  };
}

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  errors: Array<{
    serviceId: string;
    reason: "invalid_id" | "content_sanitized" | "too_long" | "empty_after_sanitize";
  }>;
  warnings: string[];
  notes?: Record<string, string>;
}

/** インポートファイルの判別結果（v1 平文 / v2 暗号化バックアップ） */
export type ParsedImportFile =
  | { kind: "plaintext"; data: unknown }
  | { kind: "encrypted"; envelope: EncryptedNotesEnvelope };

// ===== Layer 1: ファイルサイズ検証（DoS防止） =====
export const validateFileSize = (file: File): void => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(i18n.t("privateMemo.utils.fileTooLarge", { max: MAX_FILE_SIZE / 1024 / 1024 }));
  }
};

// ===== Layer 2: データ構造検証（型安全性） =====
export function validateImportData(data: unknown): asserts data is PrivateNotesExportData {
  if (typeof data !== "object" || data === null) {
    throw new Error(i18n.t("privateMemo.utils.invalidFormat"));
  }

  const d = data as Partial<PrivateNotesExportData>;

  // 必須フィールドチェック
  if (!d.version || !d.exportedAt || !d.notes) {
    throw new Error(i18n.t("privateMemo.utils.missingFields"));
  }

  // バージョンチェック
  if (d.version !== EXPORT_VERSION) {
    throw new Error(i18n.t("privateMemo.utils.unsupportedVersion", { version: d.version }));
  }

  // notes が配列でないことを確認
  if (typeof d.notes !== "object" || Array.isArray(d.notes)) {
    throw new Error(i18n.t("privateMemo.utils.notesNotObject"));
  }

  // 各エントリの型チェック
  for (const [key, value] of Object.entries(d.notes)) {
    if (typeof key !== "string" || typeof value !== "string") {
      throw new Error(i18n.t("privateMemo.utils.invalidEntry", { key, type: typeof value }));
    }
  }
}

// ===== Layer 3: メモ件数検証（DoS防止） =====
export const validateNotesCount = (notes: Record<string, string>): void => {
  const count = Object.keys(notes).length;
  if (count > MAX_NOTES_COUNT) {
    throw new Error(i18n.t("privateMemo.utils.tooManyNotes", { max: MAX_NOTES_COUNT }));
  }
};

// ===== Layer 4: serviceId ホワイトリスト検証（データ改ざん防止） =====
export const validateServiceId = (id: string, validIds: Set<string>): boolean => {
  return validIds.has(id);
};

// ===== Layer 5: コンテンツサニタイゼーション（XSS/Unicode攻撃防止） =====

/**
 * 危険なUnicode文字の除去
 */
const sanitizeUnicode = (text: string): string => {
  return (
    text
      // ゼロ幅文字除去
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      // BiDi制御文字除去（RTL Override等）
      .replace(/[\u202A-\u202E]/g, "")
      // 制御文字除去
      // biome-ignore lint/suspicious/noControlCharactersInRegex: Intentionally matching control characters for sanitization
      .replace(/[\x00-\x1F\x7F-\x9F]/g, "")
      .trim()
  );
};

/**
 * HTML/スクリプトタグの除去（将来のXSS対策）
 */
const sanitizeHtml = (text: string): string => {
  return (
    text
      // <script>タグ除去
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      // 全HTMLタグ除去
      .replace(/<[^>]+>/g, "")
      // javascript:スキーム除去
      .replace(/javascript:/gi, "")
      // イベントハンドラ除去
      .replace(/on\w+\s*=/gi, "")
  );
};

/**
 * 統合サニタイゼーション関数（多層防御）
 */
export const sanitizeNoteContent = (content: string): string => {
  let sanitized = content;

  // Layer 1: Unicode攻撃対策
  sanitized = sanitizeUnicode(sanitized);

  // Layer 2: HTML/スクリプト除去
  sanitized = sanitizeHtml(sanitized);

  // Layer 3: 文字数制限
  sanitized = sanitized.slice(0, MAX_NOTE_LENGTH);

  return sanitized;
};

// ===== エクスポート機能 =====

/** JSON をファイルとしてダウンロードさせる共通配管 */
const downloadJson = (filename: string, value: unknown): void => {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportPrivateNotes = (notes: Record<string, string>, services: AnyService[]): void => {
  const data: PrivateNotesExportData = {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    notes,
    metadata: {
      totalNotes: Object.keys(notes).length,
      notesList: Object.entries(notes).map(([serviceId, content]) => ({
        serviceId,
        serviceName: services.find((s) => s.id === serviceId)?.name,
        noteLength: content.length,
      })),
    },
  };

  downloadJson(`ai-deck-private-notes_${new Date().toISOString().split("T")[0]}.json`, data);
};

// ===== 暗号化バックアップのエクスポート =====
/** v2 封筒をそのまま書き出す（パスフレーズなしでは読めない安全な既定エクスポート） */
export const exportEncryptedBackup = (envelope: EncryptedNotesEnvelope): void => {
  downloadJson(
    `ai-deck-private-notes-encrypted_${new Date().toISOString().split("T")[0]}.json`,
    envelope,
  );
};

// ===== インポート機能 =====

/** 失敗の ImportResult を組み立てる（catch 節・呼び出し側のエラー表示で共用） */
export const importFailure = (warning: string): ImportResult => ({
  success: false,
  imported: 0,
  skipped: 0,
  errors: [],
  warnings: [warning],
});

/**
 * インポートファイルを判別する（v2 暗号化バックアップはパスフレーズ入力が必要なため、
 * 呼び出し側はここで分岐して復号後に importNotesRecord / importParsedPlaintext へ流す）。
 * ファイルサイズ超過・JSON 不正は throw する
 */
export const parseImportFile = async (file: File): Promise<ParsedImportFile> => {
  validateFileSize(file);
  const text = await file.text();
  const data: unknown = JSON.parse(text);
  if (isEncryptedEnvelope(data)) {
    return { kind: "encrypted", envelope: data };
  }
  return { kind: "plaintext", data };
};

/**
 * メモ Record を検証・サニタイズして取り込む共通経路。
 * v1（平文エクスポート）と v2（暗号化バックアップの復号結果）の両方がここに合流する
 */
export const importNotesRecord = (
  notesInput: Record<string, string>,
  mode: "merge" | "overwrite",
  currentNotes: Record<string, string>,
  validServiceIds: Set<string>,
): ImportResult => {
  const errors: ImportResult["errors"] = [];
  const warnings: string[] = [];

  try {
    validateNotesCount(notesInput);

    // === サニタイゼーション ===
    const sanitizedNotes: Record<string, string> = {};
    let validCount = 0;
    let sanitizedCount = 0;

    for (const [serviceId, content] of Object.entries(notesInput)) {
      // serviceId ホワイトリスト検証
      if (!validateServiceId(serviceId, validServiceIds)) {
        errors.push({ serviceId, reason: "invalid_id" });
        continue;
      }

      // コンテンツサニタイゼーション
      const originalContent = content;
      const sanitized = sanitizeNoteContent(content);

      // サニタイズ後に空になった場合はスキップ
      if (!sanitized.trim()) {
        errors.push({ serviceId, reason: "empty_after_sanitize" });
        continue;
      }

      // サニタイズが行われた場合は記録
      if (sanitized !== originalContent) {
        sanitizedCount++;
        errors.push({ serviceId, reason: "content_sanitized" });
      }

      sanitizedNotes[serviceId] = sanitized;
      validCount++;
    }

    // === マージ or 上書き ===
    const finalNotes =
      mode === "overwrite" ? sanitizedNotes : { ...currentNotes, ...sanitizedNotes };

    // 警告メッセージ生成
    if (sanitizedCount > 0) {
      warnings.push(i18n.t("privateMemo.utils.sanitizedNotes", { count: sanitizedCount }));
    }
    const invalidIdCount = errors.filter((e) => e.reason === "invalid_id").length;
    if (invalidIdCount > 0) {
      warnings.push(i18n.t("privateMemo.utils.skippedUnknownIds", { count: invalidIdCount }));
    }

    return {
      success: true,
      imported: validCount,
      skipped: errors.length,
      errors,
      warnings,
      notes: finalNotes,
    };
  } catch (error) {
    return importFailure(
      error instanceof Error ? error.message : i18n.t("privateMemo.utils.unknownError"),
    );
  }
};

/**
 * parseImportFile で plaintext と判別済みのデータを検証して取り込む
 * （v1 "1.0.0" エクスポート形式。後方互換のため従来どおり受け付ける）
 */
export const importParsedPlaintext = (
  data: unknown,
  mode: "merge" | "overwrite",
  currentNotes: Record<string, string>,
  validServiceIds: Set<string>,
): ImportResult => {
  try {
    validateImportData(data);
    return importNotesRecord(data.notes, mode, currentNotes, validServiceIds);
  } catch (error) {
    return importFailure(
      error instanceof Error ? error.message : i18n.t("privateMemo.utils.unknownError"),
    );
  }
};
